import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ModuleIntro, ModuleBanner } from "@/components/ModuleIntro";
import { IdrSlider } from "@/components/idr/IdrSlider";
import { IdrCles, Nav } from "@/components/idr/IdrCles";
import { IDR_ITEMS, IDR_SCALE, IDR_T } from "@/data/idr";
import { CLES_T } from "@/data/idrCles";
import { API, scrollTop } from "@/components/exo/driversExoShared";
import { IdrPatientResultats } from "@/components/idr/IdrPatientResultats";

const TOTAL = IDR_ITEMS.length;
const countAnswered = (a) => Object.keys(a).filter((k) => Number.isFinite(a[k])).length;

const Welcome = ({ t, resume, onStart }) => (
  <div className="animate-fade-up text-center">
    <ModuleBanner moduleKey="idr-50" />
    <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl text-[var(--gold)] leading-tight mb-6">{t.welcomeTitle}</h1>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-6">{t.welcomeText}</p>
    <div className="inline-flex items-center gap-2 rounded-full bg-[var(--sand)] border border-[var(--border)] px-4 py-2 text-sm text-[var(--ink-soft)] mb-6" data-testid="idr-seance-badge"><Lock size={14} /> Séance ouverte par votre praticien — vos réponses lui sont transmises directement.</div>
    <ModuleIntro moduleKey="idr-50" lang="fr" className="mb-8" />
    {resume && <p className="text-sm text-[var(--ink-soft)] mb-4" data-testid="idr-seance-resume">{CLES_T.resumeNotice}</p>}
    <button onClick={onStart} data-testid="idr-btn-start" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{resume ? "Reprendre" : t.start}</button>
  </div>
);

const Consigne = ({ t, scale, onPrev, onNext }) => (
  <div className="animate-fade-up">
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.consigne}</h2>
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 text-[var(--ink)] leading-relaxed space-y-3">
      <p>{t.consigneP1}</p>
      <p>{t.scaleIntro}</p>
      <div className="grid grid-cols-5 gap-2">{scale.map((s) => <div key={s.value} className="rounded-xl bg-white/70 border border-[var(--border)] p-3 text-center"><div className="text-xl font-serif-title text-[var(--gold)]">{s.value} %</div><div className="text-[0.68rem] leading-tight text-[var(--ink-soft)] mt-1">{s.label}</div></div>)}</div>
      <p className="text-sm text-[var(--ink-soft)]">Les 50 affirmations vous sont présentées une par une. Vos résultats sont réservés à votre praticien, qui les lira avec vous.</p>
    </div>
    <Nav onPrev={onPrev} onNext={onNext} nextLabel={t.begin} testid="idr-consigne" />
  </div>
);

const Item = ({ t, scale, n, answers, sending, onAnswer, onPrev, onNext }) => {
  const it = IDR_ITEMS[n];
  const pct = Math.round((countAnswered(answers) / TOTAL) * 100);
  return (
    <div className="animate-fade-up" key={it.n}>
      <div className="mb-8" data-testid="idr-progress">
        <div className="flex items-baseline justify-between mb-2"><span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)]">Affirmation {n + 1} sur {TOTAL}</span><span className="font-serif-title text-2xl text-[var(--gold)]" data-testid="idr-progress-pct">{pct} %</span></div>
        <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden"><div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <IdrSlider id={`idr-${it.n}`} statement={it.fr} index={it.n} scale={scale} value={answers[it.n]} onChange={(v) => onAnswer(it.n, v)} anchorLeft={t.scaleLeft} anchorRight={t.scaleRight} hint={t.sliderHint} />
      <Nav onPrev={onPrev} onNext={onNext} disabled={!Number.isFinite(answers[it.n]) || sending} nextLabel={n === TOTAL - 1 ? (sending ? "Transmission…" : "Transmettre mes réponses") : t.next} testid="idr-item" />
    </div>
  );
};

const Thanks = ({ onNext }) => (
  <div className="animate-fade-up text-center py-6" data-testid="idr-seance-thanks">
    <CheckCircle2 size={44} className="mx-auto text-[#2F7A55] mb-4" />
    <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{CLES_T.thanksTitle}</h2>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-8">{CLES_T.thanksText}</p>
    <button onClick={onNext} data-testid="idr-btn-cles" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105">{CLES_T.thanksBtn}</button>
  </div>
);

const Done = ({ token, idr, shared }) => (
  <div className="animate-fade-up text-center py-6" data-testid="idr-seance-done">
    <CheckCircle2 size={44} className="mx-auto text-[#2F7A55] mb-4" />
    <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{CLES_T.doneTitle}</h2>
    {shared && Object.keys(idr?.answers || {}).length >= 50
      ? <div className="mb-8"><IdrPatientResultats idr={idr} /></div>
      : <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-6" data-testid="idr-seance-done-text">{CLES_T.doneText}</p>}
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 max-w-xl mx-auto">
      <p className="text-[var(--ink)] mb-4">{CLES_T.doneExo}</p>
      <a href={`/p/drivers-exo?seance=${token}`} data-testid="idr-btn-exo" className="inline-flex px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105">{CLES_T.doneExoBtn}</a>
    </div>
  </div>
);

export default function IdrSeanceModule({ token }) {
  const t = IDR_T.fr;
  const scale = IDR_SCALE.fr;
  const [state, setState] = useState("loading"); // loading | invalid | welcome | consigne | items | thanks | cles | done
  const [idr, setIdr] = useState({});
  const [shared, setShared] = useState(false);
  const [answers, setAnswers] = useState({});
  const [n, setN] = useState(0);
  const timer = useRef(null);
  const [sending, setSending] = useState(false);
  const save = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/drivers-entourage/seances/${token}/idr`, payload);
      if (!data.found) { toast.error("Lien de séance introuvable — demandez un nouveau lien à votre praticien"); return false; }
      return true;
    } catch { toast.error("Transmission impossible — vérifiez votre connexion et réessayez"); return false; }
  };

  useEffect(() => {
    axios.get(`${API}/drivers-entourage/seances/${token}`).then(({ data }) => {
      if (!data.found) return setState("invalid");
      const d = data.idr || {};
      setIdr(d);
      setShared(!!data.share_results);
      setAnswers(d.answers || {});
      setState(d.step === "done" ? "done" : "welcome");
    }).catch(() => setState("invalid"));
  }, [token]);

  // Écran de fin : on surveille l'ouverture des résultats par le praticien.
  useEffect(() => {
    if (state !== "done" || shared) return undefined;
    const id = setInterval(() => axios.get(`${API}/drivers-entourage/seances/${token}`).then(({ data }) => { if (data.found && data.share_results) setShared(true); }).catch(() => {}), 10000);
    return () => clearInterval(id);
  }, [state, shared, token]);

  const resume = !!idr.step && idr.step !== "done";
  const start = () => { scrollTop(); if (idr.step && idr.step !== "items") return setState("cles"); setN(Math.min(countAnswered(answers), TOTAL - 1)); setState(answers && countAnswered(answers) > 0 ? "items" : "consigne"); };
  const onAnswer = (k, v) => {
    const next = { ...answers, [k]: v };
    setAnswers(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save({ answers: next, step: "items" }), 700);
  };
  const nextItem = async () => {
    scrollTop();
    if (n < TOTAL - 1) return setN(n + 1);
    clearTimeout(timer.current);
    setSending(true);
    const ok = await save({ answers, step: "cles" });
    setSending(false);
    if (!ok) return;
    setIdr((p) => ({ ...p, step: "cles" }));
    setState("thanks");
  };

  if (state === "loading") return <Layout><p className="text-center text-[var(--ink-soft)] py-20">Chargement…</p></Layout>;
  if (state === "invalid") return <Layout><div className="text-center py-20"><h1 className="font-serif-title text-3xl text-[var(--gold)] mb-3">Lien de séance invalide</h1><p className="text-[var(--ink-soft)]">Merci de demander un nouveau lien à votre praticien.</p></div></Layout>;
  return (
    <Layout>
      {state === "welcome" && <Welcome t={t} resume={resume} onStart={start} />}
      {state === "consigne" && <Consigne t={t} scale={scale} onPrev={() => setState("welcome")} onNext={() => { setState("items"); scrollTop(); }} />}
      {state === "items" && <Item t={t} scale={scale} n={n} answers={answers} sending={sending} onAnswer={onAnswer} onPrev={() => { scrollTop(); n > 0 ? setN(n - 1) : setState("consigne"); }} onNext={nextItem} />}
      {state === "thanks" && <Thanks onNext={() => { setState("cles"); scrollTop(); }} />}
      {state === "cles" && <IdrCles idr={idr} onSave={save} onDone={() => setState("done")} />}
      {state === "done" && <Done token={token} idr={idr} shared={shared} />}
    </Layout>
  );
}
